/**
 * DRM configuration for the player. Two key-delivery models are supported, and
 * they can be combined:
 *
 *  1. Clear-key / AES-128 / SAMPLE-AES — the KEY URL is in the manifest
 *     (`#EXT-X-KEY:METHOD=AES-128,URI="https://…/key"`). hls.js fetches the key
 *     from that URL automatically; you don't need `widevine`/`playready` at all.
 *     Use `keyXhrSetup` only if the key server needs auth (token/header).
 *
 *  2. EME multi-DRM (Widevine/PlayReady via hls.js; FairPlay via ./fairplay.ts).
 *     The manifest carries init data (PSSH/KID); the LICENSE URL is configured
 *     here (license servers are separate).
 *
 * DRM and P2P coexist transparently: the P2P fragment loader passes encrypted
 * media bytes through unchanged; the CDM/hls.js decrypts downstream. Key/license
 * requests are NOT P2P — they go over HTTP (hls.js key loader / EME controller),
 * so "load key via a URL in the manifest" is fully compatible with P2P.
 */
export interface DrmConfig {
  /** Widevine (Chrome, Firefox, Android, many Smart TVs). `certificateUrl` optional. */
  widevine?: { licenseUrl: string; certificateUrl?: string };
  /** PlayReady (Edge, Windows, Xbox, some Smart TVs). */
  playready?: { licenseUrl: string };
  /** FairPlay (Safari, iOS). Certificate REQUIRED. Handled on the native-HLS path. */
  fairplay?: { licenseUrl: string; certificateUrl: string };
  /** EME robustness levels (e.g. "SW_SECURE_CRYPTO" / "HW_SECURE_ALL"). */
  drmSystemOptions?: { audioRobustness?: string; videoRobustness?: string };
  /** Model 2: customize the EME license request (add auth headers / tokens). */
  licenseXhrSetup?: (xhr: XMLHttpRequest, url: string) => void | Promise<void>;
  /**
   * Model 1: customize the AES-128 / clear-key KEY request whose URL comes from
   * the manifest's `#EXT-X-KEY URI` (attach a token/header). Maps to hls.js
   * `xhrSetup`, so it also applies to playlist loads via hls.js's default loader
   * — but NOT to the P2P media-fragment loader (media doesn't need key auth).
   */
  keyXhrSetup?: (xhr: XMLHttpRequest, url: string) => void;
}

/** True when any DRM that runs through hls.js (EME systems OR manifest key auth) is configured. */
export function hasHlsDrm(drm?: DrmConfig): boolean {
  return !!(drm && (drm.widevine || drm.playready || drm.keyXhrSetup));
}

/**
 * Build the hls.js config for the hls.js-path DRM in a `DrmConfig`: EME
 * (Widevine/PlayReady) and/or the manifest-driven AES key request. Returns a
 * loose object to spread into `new Hls({...})`. FairPlay is NOT here (native path).
 */
export function hlsDrmConfig(drm: DrmConfig): Record<string, unknown> {
  const cfg: Record<string, unknown> = {};

  // Model 2 — EME (Widevine/PlayReady).
  if (drm.widevine || drm.playready) {
    const drmSystems: Record<string, { licenseUrl: string; serverCertificateUrl?: string }> = {};
    if (drm.widevine) {
      drmSystems["com.widevine.alpha"] = {
        licenseUrl: drm.widevine.licenseUrl,
        ...(drm.widevine.certificateUrl ? { serverCertificateUrl: drm.widevine.certificateUrl } : {}),
      };
    }
    if (drm.playready) drmSystems["com.microsoft.playready"] = { licenseUrl: drm.playready.licenseUrl };
    cfg.emeEnabled = true;
    cfg.drmSystems = drmSystems;
    if (drm.drmSystemOptions) cfg.drmSystemOptions = drm.drmSystemOptions;
    if (drm.licenseXhrSetup) {
      const user = drm.licenseXhrSetup;
      cfg.licenseXhrSetup = function (xhr: XMLHttpRequest, url: string) { return user(xhr, url); };
    }
  }

  // Model 1 — AES/clear-key key request auth (key URL from the manifest).
  if (drm.keyXhrSetup) {
    const key = drm.keyXhrSetup;
    cfg.xhrSetup = function (xhr: XMLHttpRequest, url: string) { key(xhr, url); };
  }

  return cfg;
}
